import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Slide from '@mui/material/Slide';
import { LoadingButton } from '@mui/lab';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import SearchDoctor from '../../components/Autocomplete/SearchDoctor';
import SearchPatient from '../../components/Autocomplete/SearchPatient';
import { TextField, Stack, Box } from '@mui/material';
import { addAppointment, updateAppointment } from '../../apis/appointmentSlice';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const defaultForm = () => ({
  doctor: null,
  patient: null,
  date: new Date(),
  startTime: '09:00',
  endTime: '10:00',
  patientFormId: null,
});

export default function PatientFormDialog(props) {
  const {
    open,
    setOpen,
    operationMode,
    setOperationMode,
    callApi,
    editData,
    selectedPatient,
    setSelectedPatient,
    setSelectedPatientFormId,
    selectedPatientFormId,
  } = props;
  const dispatch = useDispatch();

  const { apptLoading } = useSelector((state) => state.appointmentData);

  const [data, setData] = useState(defaultForm);

  React.useEffect(() => {
    if (selectedPatient) {
      setData((prev) => ({
        ...prev,
        patient: {
          label: selectedPatient.name,
          value: selectedPatient._id,
          ...selectedPatient,
        },
      }));
    }
  }, [selectedPatient]);

  React.useEffect(() => {
    if (selectedPatientFormId) {
      setData((prev) => ({
        ...prev,
        patientFormId: selectedPatientFormId,
      }));
    }
  }, [selectedPatientFormId]);

  React.useEffect(() => {
    if (editData && operationMode === 'Edit') {
      const apptDate = editData.appointmentDate
        ? new Date(editData.appointmentDate)
        : editData.date
          ? new Date(editData.date)
          : new Date();

      const pf = editData.patientFormId;
      const patientFormIdVal =
        pf && typeof pf === 'object' && pf._id ? pf : pf ? { _id: pf } : null;

      const d = editData.doctorId;
      const editSpecName =
        d && typeof d.docSpeciality === 'object' && d.docSpeciality?.name
          ? d.docSpeciality.name
          : '';
      setData({
        _id: editData._id,
        doctor: d
          ? {
              ...d,
              label: editSpecName ? `${d.name} — ${editSpecName}` : d.name,
              value: d._id,
              name: d.name,
              _id: d._id,
              specialityName: editSpecName,
            }
          : null,
        patient: editData.patientId
          ? {
              label: editData.patientId.name,
              value: editData.patientId._id,
              ...editData.patientId,
            }
          : null,
        date: apptDate,
        startTime: editData.startTime || '09:00',
        endTime: editData.endTime || '10:00',
        patientFormId: patientFormIdVal,
      });
    }
  }, [editData, operationMode]);

  const handleClose = () => {
    setOpen(false);
    setData(defaultForm());
    setOperationMode('Add');
    if (setSelectedPatient || setSelectedPatientFormId) {
      setSelectedPatient?.(null);
      setSelectedPatientFormId?.(null);
    }
  };

  const buildPayload = () => {
    const payload = {
      doctorId: data.doctor.value,
      patientId: data.patient.value,
      appointmentDate: data.date instanceof Date ? data.date.toISOString() : data.date,
      startTime: data.startTime || '09:00',
      endTime: data.endTime || '10:00',
    };

    const pfRaw = data.patientFormId;
    const pfId =
      pfRaw && typeof pfRaw === 'object' && pfRaw._id != null
        ? pfRaw._id
        : typeof pfRaw === 'string'
          ? pfRaw
          : null;
    if (pfId) {
      payload.patientFormId = pfId;
    }

    return payload;
  };

  const handleSubmit = async () => {
    if (!data.doctor) return toast.error('Please select a doctor');
    if (!data.patient) return toast.error('Please select a patient');

    if (operationMode === 'Add') {
      const payload = buildPayload();
      const response = await dispatch(addAppointment(payload));

      if (!response.type?.endsWith('fulfilled')) {
        toast.error(
          response.payload?.message || response.payload?.error || 'Failed to create appointment',
        );
        return;
      }
      if (response.payload?.success === false) {
        toast.error(response.payload?.message || 'Failed to create appointment');
        return;
      }
      toast.success(response.payload?.message || 'Appointment created successfully');
      handleClose();
      callApi?.();
      return;
    }

    const response = await dispatch(
      updateAppointment({
        _id: data._id,
        ...buildPayload(),
      }),
    );

    if (!response.type?.endsWith('fulfilled')) {
      toast.error(
        response.payload?.message || response.payload?.error || 'Failed to update appointment',
      );
      return;
    }
    if (response.payload?.success === false) {
      toast.error(response.payload?.message || 'Failed to update appointment');
      return;
    }
    toast.success(response.payload?.message || 'Appointment updated successfully');
    handleClose();
    callApi?.();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setData((prev) => ({
      ...prev,
      [name]: name === 'date' ? new Date(value) : value,
    }));
  };

  return (
    <React.Fragment>
      <Dialog
        open={open}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleClose}
        aria-describedby="alert-dialog-slide-description"
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle className="modalHeader">
          {operationMode === 'Add' ? 'Book appointment' : 'Edit appointment'}
        </DialogTitle>
        <DialogContent className="modalContent" sx={{ pt: 1 }}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Box>
              <SearchDoctor
                open={open}
                setData={setData}
                data={data}
                variant="outlined"
                name="doctor"
                label="Doctor"
                showSpecialityInLabel
              />
            </Box>
            <Box>
              <SearchPatient open={open} setData={setData} data={data} />
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Appointment date"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                name="date"
                value={
                  data?.date
                    ? new Date(data.date).toISOString().split('T')[0]
                    : ''
                }
                onChange={handleChange}
              />
              <TextField
                label="Start"
                type="time"
                fullWidth
                InputLabelProps={{ shrink: true }}
                name="startTime"
                value={data.startTime || '09:00'}
                onChange={handleChange}
                inputProps={{ step: 300 }}
              />
              <TextField
                label="End"
                type="time"
                fullWidth
                InputLabelProps={{ shrink: true }}
                name="endTime"
                value={data.endTime || '10:00'}
                onChange={handleChange}
                inputProps={{ step: 300 }}
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions className="modalFooter" sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" onClick={handleClose} color="error">
            Cancel
          </Button>
          <LoadingButton
            loading={apptLoading}
            variant="contained"
            onClick={handleSubmit}
            className="dialogSubmitBtn"
          >
            {operationMode === 'Add' ? 'Create appointment' : 'Save changes'}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}
